import ResourcePage from './ui/ResourcePage';
import {resourceFetch as apiFetch,reportResourceError} from './app/resourceApi';
import {ResourceEditor,Field,CreatePanel} from './ui';
import React, { useState, useEffect } from 'react';
import { API_BASE, showAppConfirm } from './api';
import { mergeStyles, theme } from './theme';

const styles = {
  stack: {
    display: 'grid',
    gap: '20px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  card: {
    ...theme.glassCard,
    padding: '24px',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  title: {
    margin: '0 0 16px',
    color: 'var(--board-text-strong, #fff)',
    fontSize: '20px',
  },
  form: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  dropIndicatorRow: { height: 0 },
  dropIndicatorCell: { padding: 0, borderBottom: 'none' },
  dropIndicatorLine: { height: '2px', background: '#60a5fa', borderRadius: '999px', boxShadow: '0 0 0 1px rgba(96,165,250,0.18), 0 0 16px rgba(96,165,250,0.34)' },
  input: {
    ...theme.input,
    marginRight: '8px',
  },
  button: {
    ...theme.button,
    ...theme.primaryButton,
  },
  deleteBtn: {
    ...theme.button,
    ...theme.dangerButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  editBtn: {
    ...theme.button,
    ...theme.mutedButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  saveBtn: {
    ...theme.button,
    ...theme.successButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  mutedBtn: {
    ...theme.button,
    ...theme.mutedButton,
    padding: '7px 12px',
    fontSize: '12px',
    marginRight: '6px',
  },
  activeBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(34,197,94,0.18)',
    border: '1px solid rgba(74,222,128,0.36)',
    color: 'var(--board-success-text, #dcfce7)',
    fontSize: '12px',
    fontWeight: 800,
  },
  inactiveBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(148,163,184,0.16)',
    border: '1px solid rgba(148,163,184,0.34)',
    color: 'var(--board-text-soft, #e2e8f0)',
    fontSize: '12px',
    fontWeight: 800,
  },
  activeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--board-text-soft, rgba(255,255,255,0.82))',
    fontSize: '13px',
    fontWeight: 700,
  },
  inactiveRow: { opacity: 0.62 },
  link: {
    color: 'var(--board-text-strong, #fff)',
    textDecoration: 'none',
    border: '1px solid var(--board-border, rgba(255,255,255,0.22))',
    borderRadius: '999px',
    padding: '7px 12px',
    display: 'inline-block',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
  },
  dragCell: {
    ...theme.td,
    width: '34px',
    textAlign: 'right',
    paddingRight: '10px',
  },
  dragHandle: {
    display: 'inline-block',
    color: 'var(--board-text-soft, rgba(226,232,240,0.74))',
    cursor: 'grab',
    userSelect: 'none',
    lineHeight: 1,
    fontSize: '18px',
    fontWeight: 700,
  },
  draggingRow: {
    opacity: 0.52,
  },
};

function Gates() {
  const [gates, setGates] = useState([]);
  const [name, setName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', is_active: true });
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [draggingGateId, setDraggingGateId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const loadGates = () => {
    apiFetch(`${API_BASE}/gates?includeInactive=true`)
      .then(res => res.json())
      .then(data => setGates(Array.isArray(data) ? data : [])).catch(reportResourceError);
  };

  useEffect(() => { loadGates(); }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addGate = async (e) => {
    e.preventDefault();
    if (!name) return false;
    await apiFetch(`${API_BASE}/gates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name })
    });
    setName('');
    loadGates();
  };

  const deleteGate = async (id) => {
    if (!await showAppConfirm('Tor dauerhaft löschen? Bestehende Zuweisungen verlieren die Tor-Verknüpfung.', {
      title: 'Tor löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${API_BASE}/gates/${id}`, { method: 'DELETE' });
    loadGates();
  };

  const startEdit = (gate) => {
    setEditId(gate.id);
    setEditForm({
      name: gate.name || '',
      is_active: gate.is_active !== false,
    });
  };

  const saveEdit = async (id) => {
    await apiFetch(`${API_BASE}/gates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm)
    });
    setEditId(null);
    loadGates();
  };

  const reorderGates = async (nextGates) => {
    const response = await apiFetch(`${API_BASE}/gates/reorder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ gate_ids: nextGates.map(gate => gate.id) }),
    });
    const data = await response.json().catch(() => []);
    setGates(Array.isArray(data) ? data : nextGates);
  };

  const moveGate = (list, fromIndex, toIndex) => {
    if (fromIndex === -1 || toIndex < 0) return list;
    const next = [...list];
    const [item] = next.splice(fromIndex, 1);
    if (!item) return list;
    next.splice(toIndex, 0, item);
    return next;
  };

  const handleDragStart = (gateId, event) => {
    if (editId !== null) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(gateId));
    setDraggingGateId(gateId);
    setDropIndex(gates.findIndex(gate => gate.id === gateId));
  };

  const handleRowDragOver = (index, event) => {
    if (draggingGateId === null) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const nextIndex = event.clientY < rect.top + rect.height / 2 ? index : index + 1;
    if (nextIndex !== dropIndex) setDropIndex(nextIndex);
  };

  const handleTableDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setDropIndex(null);
    }
  };

  const handleDrop = async (event) => {
    if (draggingGateId === null || dropIndex === null) return;
    event.preventDefault();
    const fromIndex = gates.findIndex(gate => gate.id === draggingGateId);
    if (fromIndex === -1) {
      setDraggingGateId(null);
      setDropIndex(null);
      return;
    }

    const targetIndex = dropIndex > fromIndex ? dropIndex - 1 : dropIndex;
    if (targetIndex === fromIndex) {
      setDraggingGateId(null);
      setDropIndex(null);
      return;
    }

    const nextGates = moveGate(gates, fromIndex, targetIndex);
    setDraggingGateId(null);
    setDropIndex(null);
    setGates(nextGates);
    try {
      await reorderGates(nextGates);
    } catch {
      loadGates();
    }
  };

  const handleDragEnd = () => {
    setDraggingGateId(null);
    setDropIndex(null);
  };

  const isMobile = viewportWidth < 760;
  const columnCount = 6;

  return (
    <ResourcePage title="Tore" description="Standorte, Reihenfolge und Displayzugänge." paths={['/gates']} reload={()=>loadGates()}>
      {editId!==null&&<ResourceEditor title="Tor bearbeiten" value={editForm} onClose={()=>setEditId(null)} onSave={()=>saveEdit(editId)}><Field label="Name"><input style={mergeStyles(styles.input, {
  width: '100%',
  maxWidth: '260px'
})} value={editForm.name} onChange={e => setEditForm({
  ...editForm,
  name: e.target.value
})} /></Field><Field label="Status"><label style={styles.activeToggle}>
                        <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm({
    ...editForm,
    is_active: e.target.checked
  })} />
                        Aktiv
                      </label></Field></ResourceEditor>}<CreatePanel title="Neues Tor anlegen"><form style={{ ...styles.form, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }} onSubmit={addGate}>
          <input style={{ ...styles.input, width: '100%', marginRight: 0 }} required aria-label="Name" placeholder="Name (z.B. Tor 1)" value={name} onChange={e => setName(e.target.value)} />
          <button style={{ ...styles.button, width: isMobile ? '100%' : 'auto' }} type="submit">Hinzufügen</button>
        </form></CreatePanel>

      <div style={styles.card}>
        <h2 style={styles.title}>Tore ({gates.length})</h2>
        {isMobile && (
          <p style={{ marginTop: 0, marginBottom: '16px', color: 'var(--board-text-muted, rgba(226,232,240,0.68))', fontSize: '13px', lineHeight: 1.5 }}>
            Die Reihenfolge der Tore kann aktuell nur in der Desktop-Ansicht per Drag geändert werden.
          </p>
        )}
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Dashboard</th>
                <th style={styles.th}>Aktion</th>
                <th style={{ ...styles.th, width: '34px', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody onDrop={handleDrop} onDragOver={event => draggingGateId !== null && event.preventDefault()} onDragLeave={handleTableDragLeave}>
              {gates.map((gate, index) => (
                <React.Fragment key={gate.id}>
                  {dropIndex === index && draggingGateId !== null && (
                    <tr style={styles.dropIndicatorRow}>
                      <td colSpan={columnCount} style={styles.dropIndicatorCell}>
                        <div style={styles.dropIndicatorLine}></div>
                      </td>
                    </tr>
                  )}
                  <tr
                    style={{
                      ...(gate.is_active === false ? styles.inactiveRow : {}),
                      ...(draggingGateId === gate.id ? styles.draggingRow : {}),
                    }}
                    onDragOver={event => handleRowDragOver(index, event)}
                  >
                  <td style={styles.td}>{gate.id}</td>
                  <td style={styles.td}>
                    {<strong>{gate.name}</strong>}
                  </td>
                  <td style={styles.td}>
                    {<span style={gate.is_active === false ? styles.inactiveBadge : styles.activeBadge}>
                        {gate.is_active === false ? 'Inaktiv' : 'Aktiv'}
                      </span>}
                  </td>
                  <td style={styles.td}>
                    {gate.is_active === false ? '-' : (
                      <a style={styles.link} href={`/display/gate/${gate.id}`} target="_blank" rel="noreferrer">
                        Dashboard öffnen
                      </a>
                    )}
                  </td>
                  <td style={styles.td}>
                    {<>
                        <button style={styles.editBtn} onClick={() => startEdit(gate)}>Bearbeiten</button>
                        <button style={styles.deleteBtn} onClick={() => deleteGate(gate.id).catch(reportResourceError)}>Löschen</button>
                      </>}
                  </td>
                  <td style={styles.dragCell}><div className="ws-order-actions"><button type="button" aria-label="Nach oben verschieben" title="Nach oben verschieben" disabled={index===0} onClick={()=>reorderGates(moveGate(gates,index,index-1)).catch(reportResourceError)}>↑</button><button type="button" aria-label="Nach unten verschieben" title="Nach unten verschieben" disabled={index===gates.length-1} onClick={()=>reorderGates(moveGate(gates,index,index+1)).catch(reportResourceError)}>↓</button></div>
                    {!isMobile && editId !== gate.id && (
                      <span
                        style={{ ...styles.dragHandle, cursor: draggingGateId === gate.id ? 'grabbing' : 'grab' }}
                        draggable
                        onDragStart={event => handleDragStart(gate.id, event)}
                        onDragEnd={handleDragEnd}
                        title="Tor verschieben"
                        aria-label="Tor verschieben"
                      >
                        ☰
                      </span>
                    )}
                  </td>
                </tr>
                </React.Fragment>
              ))}
              {dropIndex === gates.length && draggingGateId !== null && (
                <tr style={styles.dropIndicatorRow}>
                  <td colSpan={columnCount} style={styles.dropIndicatorCell}>
                    <div style={styles.dropIndicatorLine}></div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ResourcePage>
  );
}

export default Gates;
